import { isMedProductName } from "src/utils/plans";
import { routeConstants } from "src/utils/constants";
import {
  noomMedOfferLayer,
  paymentCheckoutLayer,
  productSwitchLayer,
} from "src/pageDefinitions/goto/constants";
import goto from "src/pageDefinitions/goto";
import { trackEvent } from "src/utils/api/tracker";
import { getActiveProgram } from "src/utils/api/userDetails";
import { captureException } from "src/utils/error";
import {
  getSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";
import getStore from "src/utils/redux/store";
import { loadActiveSubscription } from "src/utils/redux/slices/billingData";
import { isRequestSuccess } from "src/utils/redux/requestState";
import { isUserInExperimentVariation } from "@utils/api/experimentAssignment";
import { isMembershipProductName } from "@utils/membership";

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    isPostAuthCheckoutSession?: boolean;
  }
}

export function isPostAuthCheckoutSession(): boolean {
  return getSessionState("browser").isPostAuthCheckoutSession === true;
}

export type UserType =
  | "noomWeight"
  | "noomMed"
  | "freemium"
  | "free"
  | "b2b"
  | "membership";

/** Must match {@link startMeasuring} in post-auth flow (see PostAuthAnimation). */
export const POST_AUTH_ACCOUNT_CHECK_TASK_NAME = "postAuthAccountCheck";

async function isUserInFreemium(accessCode: string) {
  return isUserInExperimentVariation(
    accessCode,
    "baseline",
    "free2nx1baseline"
  );
}

async function gotoAfterPostAuth(userType: UserType) {
  updateSessionState("browser", {
    isPostAuthCheckoutSession: true,
  });
  switch (userType) {
    case "freemium":
    case "free":
    case "membership":
      await goto.layer(paymentCheckoutLayer, null, true);
      break;
    case "noomWeight": {
      await goto.layer(
        noomMedOfferLayer,
        null,
        true,
        routeConstants.direct,
        "medUpgradePayment"
      );
      break;
    }
    case "noomMed":
      await goto.layer(productSwitchLayer, null, true, routeConstants.direct);
      break;
    case "b2b":
      await goto.layer(
        paymentCheckoutLayer,
        null,
        true,
        routeConstants.b2bUpsellClinical
      );
      break;
    default:
      break;
  }
}

export async function getUserType(accessCode: string): Promise<UserType> {
  const [activeProgram, isFreemium] = await Promise.all([
    getActiveProgram(accessCode),
    isUserInFreemium(accessCode),
    getStore().dispatch(loadActiveSubscription()),
  ]);

  if (activeProgram?.businessType === "B2B") {
    return "b2b";
  }

  const slug = activeProgram.programConfigurationSlug ?? "";
  if (slug.startsWith("FREE")) {
    if (isFreemium) {
      return "freemium";
    }
    return "free";
  }

  // Reuses the same billingData.activeSubscription source of truth as the
  // med-upgrade flow's own native-purchase eligibility check.
  const { activeSubscription } = getStore().getState().billingData;
  if (!isRequestSuccess(activeSubscription) || !activeSubscription.data) {
    return "free";
  }

  if (isMembershipProductName(activeSubscription.data.productName)) {
    return "membership";
  }

  if (isMedProductName(activeSubscription.data.productName)) {
    return "noomMed";
  }

  return "noomWeight";
}

export async function navigateAfterAnimation(
  userType: UserType,
  latencyMs: number | undefined
): Promise<void> {
  trackEvent("PostAuthAccountCheckCompleted", {
    userType,
    latencyMs,
  });

  if (userType === "noomWeight") {
    trackEvent("PostAuthUpgradeEligibilityDetermined", {
      userType,
      eligible: true,
    });

    trackEvent("PostAuthUpgradeFlowInitiated", {
      userType,
    });
  } else if (userType === "noomMed") {
    trackEvent("PostAuthRoutedToProductSwitch", {
      userType,
    });
  } else {
    trackEvent("PostAuthRoutedToCheckout", {
      userType,
    });
  }

  try {
    await gotoAfterPostAuth(userType);
  } catch (err) {
    captureException(err, "PostAuthGotoFailed", { userType });
  }
}
